import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Subject, BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { setToken } from '../../../store/auth.actions';
import { Store } from '@ngrx/store';
import { LogService } from '@services/log.service';
import { environment } from '@environments/environment';
import { ErrorService } from '../../../shared/services/error.service';

const GRAPH_ENDPOINT = 'https://graph.microsoft.com/v1.0/me';

@Injectable({
	providedIn: 'root',
})
export class MsLoginServiceService {
	isIframe = false;
	loginDisplay = false;
	private readonly _destroying$ = new Subject<void>();
	profile!: ProfileType;

	constructor(
		private MsalAuthService: MsalService,
		private http: HttpClient,
		private router: Router,
		private authStore: Store<{ token: string }>,
		private logger: LogService,
		private errSr: ErrorService
	) {}

	/**
	 * check And Set Token
	 * @returns
	 */
	checkAndSetToken(): Promise<any> {
		let loginDisplay = false;
		const accounts = this.MsalAuthService.instance.getAllAccounts();

		return new Promise((resolve, reject) => {
			if (accounts.length > 0) {
				loginDisplay = true;
				const silentRequest = {
					scopes: ['user.read'],
					account: accounts[0],
					forceRefresh: true,
				};

				this.MsalAuthService.acquireTokenSilent(silentRequest).subscribe(
					(d: any) => {
						const { idToken, uniqueId, idTokenClaims } = d;

						if (idToken) {
							// Store token and user information in local storage
							this.authStore.dispatch(setToken({ token: idToken }));
							localStorage.setItem('upn', idTokenClaims.upn);
							localStorage.setItem('uid', uniqueId);
							localStorage.setItem('expireTime', idTokenClaims.exp.toString());
							this.logger.log('Token: ' + idToken);
						} else {
							this.logger.log('Token acquisition failed; force user to re-login');
							this.errSr.handleServerError(
								environment.ERROR_TYPE.UNAUTHORIZED,
								'Token Expired',
								'Your session has expired. Please login again.',
								false,
								true
							);
						}

						resolve({ loginDisplay, token: idToken });
					},
					(error) => {
						this.logger.error('Failed to acquire token:', error);
						this.errSr.handleServerError(
							environment.ERROR_TYPE.UNAUTHORIZED,
							'Token Expired',
							'Your session has expired. Please login again.',
							false,
							true
						);
						resolve({ loginDisplay, token: '' });
					}
				);
			} else {
				this.logger.error('No account found!');
				this.errSr.handleServerError(
					environment.ERROR_TYPE.UNAUTHORIZED,
					'Token Expired',
					'Your session has expired. Please login again.',
					false,
					true
				);
				resolve({ loginDisplay, token: '' });
			}
		});
	}

	/** Login status */
	private loggedInStatus = JSON.parse(localStorage.getItem('loggedIn') || 'false');

	/**
	 * Get username
	 */
	getUsername() {
		let username;
		if (localStorage.getItem('userName')) {
			username = localStorage.getItem('userName');
		} else {
			username = '';
		}
		return username;
	}

	/**
	 * Get Title Display
	 */
	getTitleDisplay() {
		let titleDisplay;
		if (localStorage.getItem('firstName') && localStorage.getItem('lastName')) {
			titleDisplay = localStorage.getItem('firstName')?.trim() + ' ' + localStorage.getItem('lastName')?.trim();
		} else {
			titleDisplay = '';
		}
		return titleDisplay;
	}

	/**
	 * Set login status
	 *
	 * @param value
	 */
	setLoginStatus(value) {
		this.loggedInStatus = value;
		localStorage.setItem('loggedIn', value);
	}

	/**
	 * Get login status
	 */
	getLoginStatus() {
		return JSON.parse(localStorage.getItem('loggedIn') || this.loggedInStatus.toString());
	}

	/** Observe logged in status behavior subject */
	ObserveLoggedInStatus = new BehaviorSubject<boolean>(false);

	/** Navigate to pin observable */
	NavigateToPin$ = this.ObserveLoggedInStatus.asObservable();

	isOpenedinIframe() {
		this.isIframe = window !== window.parent && !window.opener;
		return this.isIframe;
	}

	/**
	 * logout SSO
	 */
	async logoutSSO() {
		this.loggedInStatus = false;
		localStorage.clear();
		if (environment.requireSSO) {
			this.MsalAuthService.logoutRedirect({
				postLogoutRedirectUri: environment.SSO_CONFIG.POST_LOGOUT_REDIRECT_URI,
			});
		} else {
			await this.router.navigate(['/login']);
			window.location.reload();
		}
	}

	destroying() {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}
}

type ProfileType = {
	givenName?: string;
	surname?: string;
	userPrincipalName?: string;
	id?: string;
};
